import React, { useEffect, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { TextField } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import CommonDataGrid from '../../components/CommonDataGrid';
import Button from '../../components/Botton';
import Loader from '../../components/Loader';
import EditImg from '../../images/SideBar/Edit';
import DeleteImg from '../../images/SideBar/Delete';
import DownloadImg from '../../images/SideBar/download';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import useArticle from './useArticle';
import EditArticle from './EditArticle/EditArticle';
import { useNavigate } from 'react-router-dom';
import { getSortByTimeButtonText } from '../../util/getSortByTimeText';
import { hasPermission } from '../../utils/permissions';
import { useAppSelector } from '../../store/hooks';
import { selectUser } from '../../store/slices/authSlice';
import { getDeleteConfirmationMessage } from '../../utils/pagePermissions';
import { generateArticleWord } from '../../utils/wordGenerator';
import ApiService from '../../service/ApiService';
import { apiPaths } from '../../service/apiPaths';
import toast from 'react-hot-toast';

const Articles: React.FC = () => {
  const user = useAppSelector(selectUser);
  const canEdit = hasPermission(user, "article", "update");
  const canAdd = hasPermission(user, "article", "create");
  const canDelete = hasPermission(user, "article", "delete");

  const columns: GridColDef[] = [
    {
      field: "title",
      headerName: "Article Title",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "category",
      headerName: "Category",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "standard",
      headerName: "Education",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "courseName",
      headerName: "Stream",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: true,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      align: "center",
      headerAlign: "center",
      flex: 1,
      filterable: false,
      valueFormatter: (value: string) => {
        if (!value) return '-';
        const date = new Date(value);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      },
    },
  ];

  // Always show Actions column if user has any permission or for PDF download
  columns.push({
    field: "Actions",
    headerName: "Actions",
    align: "center",
    headerAlign: "center",
    flex: 0.8,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <div className="flex h-full items-center justify-center gap-3">
        <button
          onClick={() => handleDownloadWord(params.row._id)}
          className="text-fontColor hover:text-blue-500"
          title="Download Word"
        >
          <DownloadImg />
        </button>
        {canEdit && (
          <button
            onClick={() => handleEdit(params.row._id)}
            className="text-fontColor hover:text-primary-2"
            title="Edit"
          >
            <EditImg />
          </button>
        )}
        {canDelete && (
          <button
            onClick={() => openDeleteModal(params.row._id)}
            className="text-fontColor hover:text-red-500"
            title="Delete"
          >
            <DeleteImg />
          </button>
        )}
      </div>
    ),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<string | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const { loading, articleData, searchQuery, setSearchQuery, getArticles, handleDelete, setFilterModel, setSortingModel, rowCount, paginationModel, setPaginationModel, handleSortByTime, timeSortOrder } = useArticle(columns)
  const navigate = useNavigate()

  // Sample data structure following your format

  useEffect(() => {
    getArticles()

  }, [])


  const handleEdit = (id: string) => {
    setSelectedArticleId(id);
  };

  const handleDownloadWord = async (id: string) => {
    try {
      toast.loading('Generating Word document...', { id: 'word-generate' });

      // Fetch full article data
      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getArticle}/${id}`
      });

      if (response) {
        // Generate Word document
        await generateArticleWord(response);
        toast.success('Word document generated successfully!', { id: 'word-generate' });
      } else {
        toast.error('Failed to fetch article data', { id: 'word-generate' });
      }
    } catch (error) {
      console.error('Error generating Word document:', error);
      toast.error('Error generating Word document', { id: 'word-generate' });
    }
  };

  const handleAdd = () => {
    navigate("/create-article")
    console.log('Adding new article');
  };

  const openDeleteModal = (id: string) => {
    setArticleToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDelete = () => {
    if (articleToDelete) {
      handleDelete(articleToDelete);

      setIsModalOpen(false);

    }
  };

  const closeDeleteModal = () => {
    setIsModalOpen(false);
    setArticleToDelete(null);
  };


  return (
    <div className="mx-auto p-4">
      {loading && <Loader />}
      {selectedArticleId && canEdit ? (
        <div>
          <EditArticle articleId={selectedArticleId} setSelectedArticleId={setSelectedArticleId} onArticleUpdated={getArticles} />
        </div>
      ) : (
        <div className=" space-y-4 mt-4 bg-white p-4 rounded-b-lg shadow-md ">
          <div className="flex justify-between items-center gap-4 mr-1 mb-4">
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              placeholder="Search By"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ maxWidth: '400px' }}
            />
            <div className="flex gap-4">
              <Button
                onClick={handleSortByTime}
                className="py-1.5 px-9 rounded"
                children={getSortByTimeButtonText(timeSortOrder)}
                type="button"
              />
              {canAdd && (
                <Button
                  onClick={handleAdd}
                  className="py-1.5 px-9 rounded"
                  children="Add Article"
                  type="button"
                />
              )}
            </div>
          </div>
          <CommonDataGrid
            id='article'
            rows={articleData}
            columns={columns}
            loading={loading}
            showConfirmation={false}
            confirmationMessage={""}
            setFilterModel={setFilterModel}
            setSortingModel={setSortingModel}
            //Pagination
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onCancelDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            onConfirmDelete={function (): void {
              throw new Error("Function not implemented.");
            }}
            toolbarProps={{ toolbar: { showQuickFilter: true } }}
          />
        </div>
      )}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message={getDeleteConfirmationMessage("article")}
      />
      <Toaster />
    </div>
  );
};

export default Articles;